import { useCallback, useEffect, useState } from "react";

import { OperatorDenied, OperatorRefused } from "../operatorHttp";
import { isSessionExpired, useReportSessionExpired } from "../sessionContext";

export type LoadState<T> =
  | { kind: "loading" }
  | { kind: "ready"; data: T }
  | { kind: "failed" }
  | { kind: "denied" }
  | { kind: "notFound" };

/**
 * Loads once and again whenever `token` changes (stale data stays visible
 * during a reload). `load` must be stable; key the caller by record id.
 */
export function useAccessLoad<T>(
  load: () => Promise<T>,
  token: number,
): { state: LoadState<T>; retry: () => void } {
  const reportExpired = useReportSessionExpired();
  const [state, setState] = useState<LoadState<T>>({ kind: "loading" });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    load()
      .then((data) => {
        if (active) setState({ kind: "ready", data });
      })
      .catch((error: unknown) => {
        if (!active) return;
        if (isSessionExpired(error)) reportExpired();
        if (error instanceof OperatorDenied) {
          setState({ kind: "denied" });
        } else if (error instanceof OperatorRefused && error.status === 404) {
          setState({ kind: "notFound" });
        } else {
          setState({ kind: "failed" });
        }
      });
    return () => {
      active = false;
    };
  }, [load, token, attempt, reportExpired]);

  const retry = useCallback(() => {
    setState({ kind: "loading" });
    setAttempt((current) => current + 1);
  }, []);
  return { state, retry };
}
